import type { CSSProperties, ReactElement } from 'react';
import { cn } from '@xdocs/ui';
import styles from './BarChart.module.css';

export interface ChartPoint {
  label: string;
  value: number;
}

export interface BarChartProps {
  data: readonly ChartPoint[];
  /** Accessible summary of what the chart shows (dataviz-clean: axis
   * labels + accessible description) — the SVG itself is `aria-hidden`;
   * this label plus the visually-hidden data table below it are what a
   * screen reader actually gets. */
  ariaLabel: string;
  valueFormatter?: (value: number) => string;
  height?: number;
  /** Already-localized column headers for the screen-reader-only data
   * table (E18 — no hardcoded English literal ships to any locale). */
  categoryColumnLabel: string;
  valueColumnLabel: string;
  className?: string | undefined;
}

/**
 * Small single-series bar chart (no approved charting lib on the allowlist
 * — task spec: "build small SVG line/bar components in features/admin").
 * One axis, one hue (`--color-accent`, a design TOKEN, never a raw hex —
 * E4), thin rounded-top bars, and a screen-reader-only `<table>` fallback
 * (dataviz skill: "a table view exists" is non-negotiable for any chart).
 * Per-bar heights are the one sanctioned inline-style exception (E4: "a
 * computed value as a CSS custom property"), matching `Skeleton.tsx`'s
 * `--skeleton-w`/`-h` precedent — never a raw `style.height` px value.
 */
export function BarChart({
  data,
  ariaLabel,
  valueFormatter,
  height = 140,
  categoryColumnLabel,
  valueColumnLabel,
  className,
}: BarChartProps): ReactElement {
  const max = Math.max(1, ...data.map((point) => point.value));
  const format = valueFormatter ?? ((value: number) => String(value));
  const wrapperStyle = { '--chart-h': `${height}px` } as CSSProperties;

  return (
    <div className={cn(styles['wrapper'], className)}>
      <div className={cn(styles['bars'])} style={wrapperStyle} role="img" aria-label={ariaLabel}>
        {data.map((point) => {
          const pct = max > 0 ? (point.value / max) * 100 : 0;
          const barStyle = { '--bar-pct': `${pct}%` } as CSSProperties;
          return (
            <div key={point.label} className={cn(styles['barTrack'])} title={`${point.label}: ${format(point.value)}`}>
              <div className={cn(styles['bar'])} style={barStyle} />
            </div>
          );
        })}
      </div>
      <table className={cn(styles['srTable'])}>
        <caption>{ariaLabel}</caption>
        <thead>
          <tr>
            <th scope="col">{categoryColumnLabel}</th>
            <th scope="col">{valueColumnLabel}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((point) => (
            <tr key={point.label}>
              <th scope="row">{point.label}</th>
              <td>{format(point.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
